import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  UseFormRegister,
  UseFormResetField,
  UseFormSetValue
} from 'react-hook-form'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { FieldValues } from '../../index'
import Container from './components/Container'
import UploadIcon from './components/UploadIcon'
import fileSuccessSrc from './images/file-check-03.svg'
import fileErrorSrc from './images/file-x-03.svg'

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100 MB

const Small = styled.div<{ $disabled?: boolean }>`
  font-size: 10px;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.base.c5 : 'inherit'};
`

const FakeLink = styled(Small)<{ $disabled?: boolean }>`
  text-decoration: underline;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.base.c5 : theme.color.brand_02};
`

const Img = styled.img`
  width: 64px;
`

const DefaultMessage = styled.div<{ $disabled?: boolean }>`
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.base.c5 : 'inherit'};
`

const SuccessMessage = styled(DefaultMessage)<{
  $disabled?: boolean
}>`
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.base.c5 : theme.color.success};
`

const ErrorMessage = styled(DefaultMessage)<{
  $disabled?: boolean
}>`
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.base.c5 : theme.color.error};
`

const ClickInput = styled.input`
  display: none;
`

type UploadCoverProps = {
  setValue: UseFormSetValue<FieldValues>
  resetField: UseFormResetField<FieldValues>
  hasError: boolean
  disabled?: boolean
  register: UseFormRegister<FieldValues>
}

const UploadCover = ({
  setValue,
  resetField,
  hasError,
  disabled,
  register
}: UploadCoverProps) => {
  const { formatMessage } = useIntl()

  const inputRef = useRef<HTMLInputElement | null>(null)

  const [acceptedFile, setAcceptedFile] = useState<File | null>(null)

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setValue('cover', acceptedFiles[0], { shouldValidate: true })
      }
    },
    [setValue]
  )

  const handleFileUploadAfterClick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files![0]

      if (!file) return

      if (file && file.type === 'application/pdf') {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(
            formatMessage(
              { id: 'FileUpload.fileSizeExceeded' },
              { maxSize: '5MB' }
            )
          )
          return
        }

        setAcceptedFile(file)
        setValue('cover', file, { shouldValidate: true })
      } else {
        toast.error(
          formatMessage(
            { id: 'Form.field.fileType' },
            { format: 'PDF' }
          )
        )
      }
    },
    [setValue, formatMessage]
  )

  const resetCover = useCallback(
    () => resetField('cover'),
    [resetField]
  )

  const handleCoverClick = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    acceptedFiles: dndAcceptedFiles,
    fileRejections
  } = useDropzone({
    onDropAccepted,
    onDropRejected: resetCover,
    onFileDialogOpen: resetCover,
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
    accept: {
      'application/pdf': ['.pdf']
    }
  })

  useEffect(() => {
    if (dndAcceptedFiles.length > 0) {
      setAcceptedFile(dndAcceptedFiles[0])
    }
  }, [dndAcceptedFiles])

  function renderDropZoneState() {
    switch (true) {
      case isDragReject:
        return (
          <>
            <UploadIcon isDragReject />
            <div>
              <ErrorMessage $disabled={disabled}>
                {formatMessage({
                  id: 'Project.print.data.cover.drag.reject'
                })}
              </ErrorMessage>
              <Small $disabled={disabled}>&nbsp;</Small>
            </div>
          </>
        )
      case isDragActive:
        return (
          <>
            <UploadIcon isDragActive />
            <div>
              <div>
                {formatMessage({
                  id: 'Project.print.data.cover.drag.active'
                })}
              </div>
              <Small $disabled={disabled}>&nbsp;</Small>
            </div>
          </>
        )
      case !!acceptedFile: {
        const { name } = acceptedFile!
        return (
          <>
            <Img src={fileSuccessSrc} />
            <div>
              <SuccessMessage $disabled={disabled}>
                {formatMessage({
                  id: 'Project.print.data.cover.add.success'
                })}
              </SuccessMessage>
              <Small $disabled={disabled}>{name}</Small>
            </div>
          </>
        )
      }
      case !!fileRejections.length:
        return (
          <>
            <Img src={fileErrorSrc} />
            <div>
              <ErrorMessage $disabled={disabled}>
                {formatMessage({
                  id: 'Project.print.data.cover.error.title'
                })}
              </ErrorMessage>
              <Small $disabled={disabled}>
                {formatMessage({
                  id: 'Project.print.data.cover.error.subtitle'
                })}
              </Small>
            </div>
          </>
        )
      default:
        return (
          <>
            <UploadIcon />
            <div>
              <DefaultMessage $disabled={disabled}>
                {formatMessage({
                  id: 'Project.print.data.cover.default.title'
                })}
              </DefaultMessage>
              <FakeLink $disabled={disabled}>
                {formatMessage({
                  id: 'Project.print.data.cover.default.subtitle'
                })}
              </FakeLink>
            </div>
          </>
        )
    }
  }

  return (
    <Container
      {...getRootProps()}
      isDragActive={isDragActive}
      isDragReject={isDragReject}
      isSuccessful={!!acceptedFile}
      hasError={!!fileRejections.length || hasError}
      onClick={handleCoverClick}
    >
      <input {...getInputProps()} {...register('cover')} />
      {renderDropZoneState()}

      {/* Unfortunatelly, it's not possible to use react-dropzone to open the file upload dialog in Safari. */}
      <ClickInput
        type="file"
        ref={inputRef}
        onChange={handleFileUploadAfterClick}
      />
    </Container>
  )
}

export default UploadCover
